import { TrackerConfig, TrackerState } from '../ar-engine/ar-types';

type VisibilityPayload = { id: string; config: TrackerConfig; state: TrackerState };

interface CyberHudOptions {
  totalTrackers: number;
  title?: string;
}

interface CyberHudApi {
  markTrackerLoading(id: string, name: string): void;
  markTrackerLoaded(id: string): void;
  setTrackerVisible(id: string, visible: boolean): void;
  setStatus(text: string): void;
  handleVisibility(payload: VisibilityPayload): void;
  startPotentialMatchScanner(): void;
  setTrackerProgress(id: string, stage: string, progress: number): void;
  dispose(): void;
}

interface TrackerStatus {
  name: string;
  loaded: boolean;
  visible: boolean;
  rowEl: HTMLElement;
  dotEl: HTMLElement;
  progressStage: string;
  progressValue: number;
  progressEl?: HTMLElement;
}

interface PotentialMatch {
  x: number;
  y: number;
  strength: number;
  shape?: number;
}

/**
 * Cyberpunk-styled HUD that shows tracker loading progress and highlights potential
 * marker matches by sampling the camera feed for high-contrast areas.
 */
export function createCyberHud(options: CyberHudOptions): CyberHudApi {
  const totalTrackers = Math.max(options.totalTrackers, 1);
  const trackerStates = new Map<string, TrackerStatus>();
  let loadedCount = 0;

  const root = document.createElement('div');
  root.className = 'cyber-hud';

  const panel = document.createElement('div');
  panel.className = 'cyber-hud__panel';
  root.appendChild(panel);

  const title = document.createElement('div');
  title.className = 'cyber-hud__title';
  title.textContent = options.title ?? 'Marker Link';
  panel.appendChild(title);

  const status = document.createElement('div');
  status.className = 'cyber-hud__status';
  status.textContent = 'Initializing AR stack...';
  panel.appendChild(status);

  const metrics = document.createElement('div');
  metrics.className = 'cyber-hud__metrics';
  metrics.textContent = `Loaded 0/${totalTrackers} | Visible 0 | Candidates 0`;
  panel.appendChild(metrics);

  const progress = document.createElement('div');
  progress.className = 'cyber-hud__progress';
  const progressBar = document.createElement('div');
  progressBar.className = 'cyber-hud__progress-bar';
  progress.appendChild(progressBar);
  panel.appendChild(progress);

  const trackerList = document.createElement('div');
  panel.appendChild(trackerList);

  const matchesCanvas = document.createElement('canvas');
  matchesCanvas.className = 'cyber-hud__matches';
  const matchesCtx = matchesCanvas.getContext('2d');
  if (!matchesCtx) {
    throw new Error('Failed to create HUD canvas');
  }
  root.appendChild(matchesCanvas);

  document.body.appendChild(root);

  const resizeCanvas = () => {
    matchesCanvas.width = window.innerWidth;
    matchesCanvas.height = window.innerHeight;
  };
  resizeCanvas();
  window.addEventListener('resize', resizeCanvas);

  let scanInterval: number | null = null;
  let scanRaf: number | null = null;
  let sampleCanvas: HTMLCanvasElement | null = null;
  let sampleCtx: CanvasRenderingContext2D | null = null;
  let typingTimer: number | null = null;
  let candidateCount = 0;

  function updateProgressBar(): void {
    const pct = Math.min(1, loadedCount / totalTrackers);
    progressBar.style.width = `${(pct * 100).toFixed(0)}%`;
  }

  function addTrackerRow(id: string, name: string): void {
    if (trackerStates.has(id)) return;
    const row = document.createElement('div');
    row.className = 'cyber-hud__tracker-row';

    const dot = document.createElement('div');
    dot.className = 'cyber-hud__tracker-dot';
    row.appendChild(dot);

    const label = document.createElement('div');
    label.textContent = name;
    row.appendChild(label);

    // Add progress element
    const progress = document.createElement('div');
    progress.className = 'cyber-hud__tracker-progress';
    progress.textContent = 'Initializing...';
    progress.style.fontSize = '0.7em';
    progress.style.color = '#00f0ff';
    progress.style.marginTop = '2px';
    row.appendChild(progress);

    trackerList.appendChild(row);

    trackerStates.set(id, {
      name,
      loaded: false,
      visible: false,
      rowEl: row,
      dotEl: dot,
      progressStage: '',
      progressValue: 0,
      progressEl: progress
    });
  }

  function setTrackerProgress(id: string, stage: string, progress: number): void {
    const entry = trackerStates.get(id);
    if (!entry || entry.loaded) return;
    
    entry.progressStage = stage;
    entry.progressValue = progress;
    
    if (entry.progressEl) {
      const pct = Math.round(progress * 100);
      entry.progressEl.textContent = `${stage} (${pct}%)`;
      
      // Change color based on progress
      if (progress >= 1.0) {
        entry.progressEl.style.color = '#00ff00'; // Green when complete
      } else if (progress >= 0.7) {
        entry.progressEl.style.color = '#ffff00'; // Yellow when nearly done
      } else if (progress >= 0.3) {
        entry.progressEl.style.color = '#00f0ff'; // Cyan during progress
      } else {
        entry.progressEl.style.color = '#ff6600'; // Orange when starting
      }
    }
  }

  function markTrackerLoaded(id: string): void {
    const entry = trackerStates.get(id);
    if (!entry || entry.loaded) return;
    entry.loaded = true;
    entry.dotEl.classList.add('loaded');
    
    // Update progress to show completion
    if (entry.progressEl) {
      entry.progressEl.textContent = 'Ready';
      entry.progressEl.style.color = '#00ff00';
    }
    
    loadedCount += 1;
    updateProgressBar();
    updateMetrics();
  }

  function getFirstVisibleName(): string | null {
    for (const entry of trackerStates.values()) {
      if (entry.visible) {
        return entry.name;
      }
    }
    return null;
  }

  function setTrackerVisible(id: string, visible: boolean): void {
    const entry = trackerStates.get(id);
    if (!entry) return;
    entry.visible = visible;
    if (visible) {
      entry.dotEl.classList.add('visible');
    } else {
      entry.dotEl.classList.remove('visible');
    }

    const lockedName = getFirstVisibleName();
    status.textContent = lockedName ? `LOCKED: ${lockedName}` : 'Scanning...';
    updateMetrics();
  }

  function drawPotentialMatches(points: PotentialMatch[]): void {
    if (!matchesCtx) return;
    matchesCtx.clearRect(0, 0, matchesCanvas.width, matchesCanvas.height);
    matchesCtx.save();
    matchesCtx.lineWidth = 1.4;
    points.forEach((p, i) => {
      const shape = p.shape ?? (i % 3);
      const jitter = shape * 3;
      const size = 20 + p.strength * 16 + jitter;
      const alpha = 0.25 + p.strength * 0.55;
      matchesCtx.strokeStyle = `rgba(0, 255, 255, ${alpha})`;
      matchesCtx.shadowColor = 'rgba(0, 255, 255, 0.7)';
      matchesCtx.shadowBlur = 10;

      if (shape === 0) {
        matchesCtx.strokeRect(p.x - size / 2, p.y - size / 2, size, size);
      } else if (shape === 1) {
        matchesCtx.beginPath();
        matchesCtx.arc(p.x, p.y, size / 2, 0, Math.PI * 2);
        matchesCtx.stroke();
      } else {
        const s = size / 2;
        const c = 6;
        // Bracket corners
        matchesCtx.beginPath();
        // TL
        matchesCtx.moveTo(p.x - s, p.y - s + c);
        matchesCtx.lineTo(p.x - s, p.y - s);
        matchesCtx.lineTo(p.x - s + c, p.y - s);
        // TR
        matchesCtx.moveTo(p.x + s - c, p.y - s);
        matchesCtx.lineTo(p.x + s, p.y - s);
        matchesCtx.lineTo(p.x + s, p.y - s + c);
        // BL
        matchesCtx.moveTo(p.x - s, p.y + s - c);
        matchesCtx.lineTo(p.x - s, p.y + s);
        matchesCtx.lineTo(p.x - s + c, p.y + s);
        // BR
        matchesCtx.moveTo(p.x + s - c, p.y + s);
        matchesCtx.lineTo(p.x + s, p.y + s);
        matchesCtx.lineTo(p.x + s, p.y + s - c);
        matchesCtx.stroke();
      }
    });
    matchesCtx.restore();
  }

  function computePotentialMatches(video: HTMLVideoElement): PotentialMatch[] {
    if (!video.videoWidth || !video.videoHeight) return [];
    if (!sampleCanvas || !sampleCtx) {
      sampleCanvas = document.createElement('canvas');
      sampleCanvas.width = 180;
      sampleCanvas.height = 120;
      sampleCtx = sampleCanvas.getContext('2d');
    }
    if (!sampleCtx) return [];
    const w = sampleCanvas.width;
    const h = sampleCanvas.height;
    sampleCtx.drawImage(video, 0, 0, w, h);
    const data = sampleCtx.getImageData(0, 0, w, h).data;

    const gridX = 10;
    const gridY = 6;
    const cellW = Math.floor(w / gridX);
    const cellH = Math.floor(h / gridY);
    const matches: Array<{ x: number; y: number; variance: number }> = [];

    for (let gy = 0; gy < gridY; gy++) {
      for (let gx = 0; gx < gridX; gx++) {
        let sum = 0;
        let sumSq = 0;
        let count = 0;
        for (let y = gy * cellH; y < (gy + 1) * cellH; y++) {
          for (let x = gx * cellW; x < (gx + 1) * cellW; x++) {
            const idx = (y * w + x) * 4;
            const r = data[idx];
            const g = data[idx + 1];
            const b = data[idx + 2];
            const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
            sum += luma;
            sumSq += luma * luma;
            count++;
          }
        }
        const mean = sum / count;
        const variance = sumSq / count - mean * mean;
        matches.push({
          x: (gx + 0.5) / gridX,
          y: (gy + 0.5) / gridY,
          variance
        });
      }
    }

    // Pick strongest variance cells as "potential matches"
    const sorted = matches.sort((a, b) => b.variance - a.variance).slice(0, 12);
    const maxVar = sorted[0]?.variance ?? 1;
    return sorted.map((cell, i) => ({
      x: cell.x * matchesCanvas.width,
      y: cell.y * matchesCanvas.height,
      strength: Math.min(1, cell.variance / (maxVar || 1)),
      shape: i % 3
    }));
  }

  function findCameraVideo(): HTMLVideoElement | null {
    const videos = Array.from(document.querySelectorAll('video')) as HTMLVideoElement[];
    if (videos.length === 0) return null;
    // Pick the largest video (likely the camera feed)
    return videos.reduce((largest, v) => {
      const area = v.videoWidth * v.videoHeight;
      const largestArea = largest ? largest.videoWidth * largest.videoHeight : 0;
      return area > largestArea ? v : largest;
    }, null as HTMLVideoElement | null);
  }

  function startPotentialMatchScanner(): void {
    if (scanInterval) {
      window.clearInterval(scanInterval);
      scanInterval = null;
    }
    if (scanRaf) {
      window.cancelAnimationFrame(scanRaf);
      scanRaf = null;
    }

    const tick = () => {
      const video = findCameraVideo();
      if (video && video.readyState >= 2) {
        const points = computePotentialMatches(video);
        drawPotentialMatches(points);
        candidateCount = points.length;
        updateMetrics();
      }
      scanRaf = window.requestAnimationFrame(tick);
    };

    // Kick off with a short delay to let the video attach
    scanInterval = window.setTimeout(() => {
      tick();
    }, 150);
  }

  function handleVisibility(payload: VisibilityPayload): void {
    setTrackerVisible(payload.id, payload.state === TrackerState.VISIBLE);
    if (payload.state === TrackerState.VISIBLE) {
      setStatus(`LOCKED: ${payload.config.name}`);
    }
  }

  function updateMetrics(): void {
    const visible = Array.from(trackerStates.values()).filter(t => t.visible).length;
    metrics.textContent = `Loaded ${loadedCount}/${totalTrackers} | Visible ${visible} | Candidates ${candidateCount}`;
  }

  function setStatus(text: string): void {
    if (typingTimer) {
      window.clearInterval(typingTimer);
      typingTimer = null;
    }
    status.textContent = '';
    let index = 0;
    typingTimer = window.setInterval(() => {
      status.textContent = text.slice(0, index);
      index += 1;
      if (index > text.length) {
        if (typingTimer) {
          window.clearInterval(typingTimer);
          typingTimer = null;
        }
      }
    }, 25);
  }

  function dispose(): void {
    if (scanInterval) {
      window.clearInterval(scanInterval);
    }
    if (scanRaf) {
      window.cancelAnimationFrame(scanRaf);
    }
    if (typingTimer) {
      window.clearInterval(typingTimer);
    }
    window.removeEventListener('resize', resizeCanvas);
    root.remove();
  }

  return {
    markTrackerLoading: (id: string, name: string) => addTrackerRow(id, name),
    markTrackerLoaded,
    setTrackerVisible,
    setStatus,
    handleVisibility,
    startPotentialMatchScanner,
    setTrackerProgress,
    dispose
  };
}
